import React, { useEffect, useRef, useState } from "react";

import * as yup from "yup";
import { Autocomplete, Box, Button, Paper, TextField, Typography } from "@mui/material";
import { ProgressOverlay } from "components/Progress-overlay/ProgressOverlay";
import { CreateAssetClass } from "components/Prompts";
import { FormikProps } from "formik";
import { useActionHook, useModals } from "hooks";
import { usePrompt } from "hooks";
import { IApiServiceError, useDealActions, useTaskDealsSelect } from "store";
import { IAssetClass } from "store/types";

import { currenciesArray } from "../../../../constants";
import { IDealsOptions, IInvestorsOptions, IRoundOptions, IValues } from "../AddNewLine/AddNewLine";

interface Props extends Pick<FormikProps<IValues>, "setFieldValue" | "values"> {
  variant: "deal" | "investor" | "round" | null;
  onClose: () => void;
  dealsOptionsState: {
    dealsOptions: IDealsOptions[];
    setDealsOptions: React.Dispatch<React.SetStateAction<IDealsOptions[]>>;
  };
  roundsOptionsState: {
    roundsOptions: IRoundOptions[];
    setRoundsOptions: React.Dispatch<React.SetStateAction<IRoundOptions[]>>;
  };
  investorsOptionsState: {
    investorsOptions: IInvestorsOptions[];
    setInvestorsOptions: React.Dispatch<React.SetStateAction<IInvestorsOptions[]>>;
  };
  onDealChangeHandler: (value: IDealsOptions | null) => void;
}

export const CreateModal: React.FC<Props> = ({
  variant,
  onClose,
  setFieldValue,
  values,
  dealsOptionsState,
  roundsOptionsState,
  investorsOptionsState,
  onDealChangeHandler
}) => {
  const { errorModal } = useModals();
  const openPrompt = usePrompt();
  const deals = useTaskDealsSelect();
  const { getAssetClassesAction } = useDealActions();
  const [isValidEmail, setIsValidEmail] = useState({ isValid: false, error: "" });
  const [isNameExist, setIsNameExist] = useState<boolean>(false);
  const assetClassInputRef = useRef<HTMLInputElement>(null);

  const {
    data: assetClasses = [],
    loading: assetClassesLoading,
    action: getAssetClasses,
    setData: setAssetClasses
  } = useActionHook(getAssetClassesAction);

  const checkExitingDealTitle = (title: string) => {
    if (deals.some((deal) => deal.title === title)) {
      setIsNameExist(true);
      return;
    }
    setIsNameExist(false);
  };

  const emailValidationSchema = yup.object().shape({
    email: yup.string().email("Invalid email address")
  });
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setFieldValue("investor.email", email);
    emailValidationSchema
      .validate({ email })
      .then(() => {
        setIsValidEmail({ isValid: true, error: "" });
      })
      .catch((error) => {
        setIsValidEmail({ isValid: false, error: error.errors[0] });
      });
  };

  useEffect(() => {
    if (variant === "deal") {
      setFieldValue("deal", null);
      setFieldValue("deal.title", "");
    }
    if (variant === "round") {
      setFieldValue("round", null);
      setFieldValue("round.name", "");
    }
    if (variant === "investor") {
      setFieldValue("investor", null);
      setFieldValue("investor.name", "");
    }
    getAssetClasses();
  }, [variant]);

  const createAssetClass = async () => {
    const inputValue = assetClassInputRef.current?.value || "";

    const result = await openPrompt({
      Component: CreateAssetClass,
      props: {
        initialValue: inputValue
      }
    });

    if (!result) return;

    try {
      setAssetClasses((prev: IAssetClass[] = []) => [...prev, result]);
      setTimeout(() => {
        setFieldValue("deal.asset_class", result);
      }, 100);
    } catch (error) {
      errorModal({ msg: (error as IApiServiceError)?.errors?.name?.[0] ?? "Something went wrong" });
    }
  };

  return (
    <ProgressOverlay
      containerSX={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        borderRadius: "8px",
        backgroundColor: "#fff",
        padding: "30px"
      }}
      inProgress={assetClassesLoading}
    >
      {variant === "deal" && (
        <Box>
          <Typography style={{ fontSize: "16px", color: "#0a3142", marginBottom: "5px" }}>Create New Deal</Typography>
          <div style={{ width: "315px", backgroundColor: "#dae0e2", height: "1px", marginBottom: "14px" }} />
          <Box
            sx={{
              height: "228px",
              paddingBottom: "28px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between"
            }}
          >
            <TextField
              sx={{
                ".MuiFormHelperText-root": {
                  bottom: "-17px",
                  position: "absolute",
                  color: "red",
                  fontSize: "11px",
                  marginLeft: "0px"
                }
              }}
              required
              fullWidth
              label="Deal Name"
              error={isNameExist}
              helperText={isNameExist ? "Deal name already exists" : ""}
              onChange={(e) => {
                setFieldValue("deal.title", e.target.value);
                checkExitingDealTitle(e.target.value);
              }}
            />
            <Autocomplete
              fullWidth
              onChange={(_, value) => setFieldValue("deal.asset_class", value)}
              getOptionLabel={(option) => option.account_alias || option.name}
              options={assetClasses}
              value={values?.deal?.asset_class || null}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.id}>
                    <Typography>{option.account_alias || option.name}</Typography>
                  </li>
                );
              }}
              slotProps={{
                paper: {
                  component: ({ children }) => {
                    return (
                      <Paper
                        sx={{
                          position: "absolute",
                          backgroundColor: "white",
                          width: "100%",
                          zIndex: "10",
                          borderRadius: "10px",
                          "& .MuiAutocomplete-option:hover": {
                            backgroundColor: "#edf1f9"
                          }
                        }}
                      >
                        <>
                          {children}
                          <Button
                            onMouseDown={(e) => {
                              e.preventDefault();
                              createAssetClass();
                            }}
                            sx={{
                              display: "flex",
                              justifyContent: "flex-start",
                              height: "50px",
                              backgroundColor: "#fff",
                              boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                              borderRadius: "0 0 10px 10px",
                              zIndex: 100
                            }}
                            fullWidth
                          >
                            Add Asset Class
                          </Button>
                        </>
                      </Paper>
                    );
                  }
                }
              }}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    inputRef={assetClassInputRef}
                    label="Select Asset Class"
                    placeholder="Choose an Option"
                    required
                  />
                );
              }}
            />
            <Autocomplete
              options={currenciesArray}
              onChange={(_, option) => setFieldValue("deal.currency", option)}
              disableClearable
              renderInput={(params) => (
                <TextField {...params} fullWidth required InputLabelProps={{ shrink: true }} label="Currency" />
              )}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={() => {
                setFieldValue("deal", null);
                onClose();
              }}
              sx={{ marginRight: "20px" }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              disabled={!values?.deal?.title || !values?.deal?.asset_class || !values?.deal?.currency || isNameExist}
              onClick={() => {
                if (!values?.deal?.title) return;
                dealsOptionsState.setDealsOptions([
                  {
                    ...values?.deal
                  },
                  ...dealsOptionsState.dealsOptions
                ]);
                onDealChangeHandler(values?.deal);
                onClose();
              }}
              variant="contained"
            >
              Create
            </Button>
          </Box>
        </Box>
      )}
      {variant === "round" && (
        <Box>
          <Typography style={{ fontSize: "16px", color: "#0a3142", marginBottom: "5px" }}>Create New Round</Typography>
          <div style={{ width: "315px", backgroundColor: "#dae0e2", height: "1px", marginBottom: "14px" }} />
          <Box
            sx={{
              paddingBottom: "28px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between"
            }}
          >
            <TextField
              required
              fullWidth
              label="Round Name"
              onChange={(e) => setFieldValue("round.name", e.target.value)}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={() => {
                setFieldValue("round", null);
                onClose();
              }}
              sx={{ marginRight: "20px" }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              disabled={!values?.round?.name}
              onClick={() => {
                if (!values?.round?.name) return;
                roundsOptionsState.setRoundsOptions([
                  {
                    ...values?.round
                  },
                  ...roundsOptionsState.roundsOptions
                ]);
                setFieldValue("round", {
                  ...values.round
                });
                onClose();
              }}
              variant="contained"
            >
              Create
            </Button>
          </Box>
        </Box>
      )}
      {variant === "investor" && (
        <Box>
          <Typography style={{ fontSize: "16px", color: "#0a3142", marginBottom: "5px" }}>
            Create New Investor
          </Typography>
          <div style={{ width: "315px", backgroundColor: "#dae0e2", height: "1px", marginBottom: "35px" }} />
          <Box
            sx={{
              height: "263px",
              paddingBottom: "28px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between"
            }}
          >
            <TextField
              required
              fullWidth
              label="Investor Name"
              onChange={(e) => setFieldValue("investor.name", e.target.value)}
            />
            <TextField
              sx={{
                ".MuiFormHelperText-root": {
                  bottom: "-17px",
                  position: "absolute",
                  color: "red",
                  fontSize: "11px",
                  marginLeft: "0px"
                }
              }}
              fullWidth
              label="Email Address"
              onChange={handleEmailChange}
              helperText={isValidEmail.error}
            />
            <TextField
              fullWidth
              label="Phone Number"
              onChange={(e) => setFieldValue("investor.phone", e.target.value)}
            />
            <TextField fullWidth label="Company" onChange={(e) => setFieldValue("investor.company", e.target.value)} />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={() => {
                setFieldValue("investor", null);
                onClose();
              }}
              sx={{ marginRight: "20px" }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              disabled={!values?.investor?.name || !!isValidEmail.error}
              onClick={() => {
                if (!values?.investor?.name) return;
                investorsOptionsState.setInvestorsOptions([
                  {
                    ...values?.investor
                  },
                  ...investorsOptionsState.investorsOptions
                ]);
                setFieldValue("investor", {
                  ...values.investor
                });
                onClose();
              }}
              variant="contained"
            >
              Create
            </Button>
          </Box>
        </Box>
      )}
    </ProgressOverlay>
  );
};
